import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { OutboundItemRow, OutboundRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | 'NEW' | 'PICKING' | 'DONE';

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待出库', color: 'default' },
  PICKING: { text: '拣货中', color: 'processing' },
  PACKED: { text: '已打包', color: 'processing' },
  DONE: { text: '已出库', color: 'green' },
  CANCELED: { text: '已取消', color: 'default' }
};

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待出库', value: 'NEW' },
  { label: '拣货中', value: 'PICKING' },
  { label: '已出库', value: 'DONE' }
];

/** 出库单：列表 + 详情 + 确认出库（确认会扣减库存并写流水）。 */
export default function OutboundList() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['outbound', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.outbound.list({ lastId: pageParam, size: PAGE_SIZE, outboundStatus: status === 'ALL' ? undefined : status }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['outbound', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.outbound.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const confirm = useMutation({
    mutationFn: () => hq.outbound.confirm(currentId ?? ''),
    onSuccess: async (result) => {
      message.success(`出库完成：${result.bizNo}，共 ${result.totalQty}`);
      await queryClient.invalidateQueries({ queryKey: ['outbound'] });
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '确认出库失败')
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<OutboundRow> = [
    {
      title: '出库单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '发货仓', dataIndex: 'warehouseName', width: 140, render: (value: string | null) => value || '-' },
    { title: '出库类型', dataIndex: 'outType', width: 130, render: (value: string) => value || '-' },
    { title: '领用科室', dataIndex: 'targetDeptName', width: 150, render: (value: string | null) => value || '-' },
    { title: '来源单号', dataIndex: 'sourceBizNo', width: 200, render: (value: string | null) => value || '-' },
    {
      title: '入即出配对',
      dataIndex: 'pairBizNo',
      width: 240,
      render: (value: string | null, row) =>
        row.outType === 'OUTER_PASS' || row.isPassThrough === 1 ? (
          <Space size={4}>
            <Tag color="purple">零库存直通</Tag>
            <Typography.Text copyable={Boolean(value)}>{value || '-'}</Typography.Text>
          </Space>
        ) : (
          '-'
        )
    },
    {
      title: '状态',
      dataIndex: 'outboundStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    { title: '业务时间', dataIndex: 'bizDate', width: 160, render: (value: string) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<OutboundItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 130, render: (value: string | null) => value || '-' },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    { title: '计划', dataIndex: 'planQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '已拣', dataIndex: 'pickQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '实出', dataIndex: 'outQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const canConfirm = detail.data ? !['DONE', 'CANCELED'].includes(detail.data.outbound.outboundStatus) : false;

  return (
    <Card
      className="page-card"
      title="出库单"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter> value={status} options={FILTERS} onChange={(value) => setStatus(value)} style={{ marginBottom: 12 }} />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="出库单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<OutboundRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无出库单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.outbound.bizNo ?? '出库单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={880}
        extra={
          canConfirm ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={confirm.isPending}
              onClick={() =>
                modal.confirm({
                  title: '确认出库',
                  content: '确认后将按明细扣减库存并写入库存流水，不可撤销。',
                  okText: '确认出库',
                  onOk: () => confirm.mutateAsync()
                })
              }
            >
              确认出库
            </Button>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.outbound.outboundStatus]?.text ?? detail.data.outbound.outboundStatus}
              </Descriptions.Item>
              <Descriptions.Item label="发货仓">{detail.data.outbound.warehouseName || '-'}</Descriptions.Item>
              <Descriptions.Item label="领用科室">{detail.data.outbound.targetDeptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="来源单号">{detail.data.outbound.sourceBizNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="入即出配对单号">{detail.data.outbound.pairBizNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.outbound.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="金额合计">{formatAmount(detail.data.outbound.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="业务时间">{formatDisplayTime(detail.data.outbound.bizDate)}</Descriptions.Item>
              <Descriptions.Item label="备注">{detail.data.outbound.remark || '-'}</Descriptions.Item>
            </Descriptions>
            <Table<OutboundItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
